import type { ReactNode } from "react"
import { ButtonBase, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

const useStyles = makeStyles()((theme) => ({
    row: {
        width: "100%",
        padding: ".75rem 1rem",
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.slate[100]}`,
        textAlign: "left",
        background: theme.palette.tailwind.slate[50],
        "&:hover": {
            background: theme.palette.tailwind.slate[100],
        },
    },
    rowHighlighted: {
        background: theme.palette.tailwind.emerald[50],
        "&:hover": {
            background: theme.palette.tailwind.emerald[100],
        },
    },
    lead: {
        width: "9rem",
        flexShrink: 0,
        fontWeight: 600,
    },
    main: {
        flex: 1,
        minWidth: 0,
    },
    title: {
        fontWeight: 600,
    },
    trailing: {
        minWidth: "6rem",
        flexShrink: 0,
    },
    tag: {
        display: "flex",
        justifyContent: "flex-end",
        minWidth: "5rem",
        flexShrink: 0,
    },
}))

type MeetingRoomsListRowProps = Readonly<{
    lead: ReactNode
    title: ReactNode
    chip: ReactNode
    meta: ReactNode
    metaTags?: ReactNode
    trailing: ReactNode
    tag?: ReactNode
    isHighlighted: boolean
    onClick: () => void
}>

export function MeetingRoomsListRow({
    lead,
    title,
    chip,
    meta,
    metaTags,
    trailing,
    tag,
    isHighlighted,
    onClick,
}: MeetingRoomsListRowProps) {
    const { classes, cx } = useStyles()

    return (
        <Stack
            component={ButtonBase}
            direction="row"
            alignItems="center"
            spacing="1rem"
            className={cx(classes.row, isHighlighted && classes.rowHighlighted)}
            onClick={onClick}
        >
            <Typography variant="body2" noWrap className={classes.lead}>
                {lead}
            </Typography>
            <Stack spacing=".25rem" className={classes.main}>
                <Typography variant="body2" noWrap className={classes.title}>
                    {title}
                </Typography>
                <Stack direction="row" alignItems="center" spacing=".5rem">
                    {chip}
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {meta}
                    </Typography>
                    {metaTags}
                </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" noWrap className={classes.trailing}>
                {trailing}
            </Typography>
            {tag !== undefined && <div className={classes.tag}>{tag}</div>}
        </Stack>
    )
}
